import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import AnalyticsOverview from '../components/admin/AnalyticsOverview';
import ClipManager from '../components/admin/ClipManager';
import ModelManager from '../components/admin/ModelManager';
import UserManager from '../components/admin/UserManager';
import UploadModeration from '../components/admin/UploadModeration';

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'clips', label: 'Clips' },
  { key: 'models', label: 'Models' },
  { key: 'users', label: 'Users' },
  { key: 'uploads', label: 'Uploads' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export default function AdminPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = (searchParams.get('tab') as TabKey) || 'overview';

  useEffect(() => {
    if (!user || !isAdmin) {
      navigate('/');
    }
  }, [user, isAdmin, navigate]);

  if (!user || !isAdmin) return null;

  const handleTabChange = (tab: TabKey) => {
    setSearchParams({ tab });
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return <AnalyticsOverview />;
      case 'clips':
        return <ClipManager />;
      case 'models':
        return <ModelManager />;
      case 'users':
        return <UserManager />;
      case 'uploads':
        return <UploadModeration />;
      default:
        return <AnalyticsOverview />;
    }
  };

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2">Admin Dashboard</h1>
        <p className="text-arena-muted">
          Manage clips, models, users, and uploads
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-arena-border mb-8">
        <nav className="flex gap-1 -mb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-arena-accent text-arena-accent'
                  : 'border-transparent text-arena-muted hover:text-arena-text hover:border-arena-border'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {renderTabContent()}
    </div>
  );
}
